import { db, auth } from "@/firebase";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  arrayUnion,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { CallerIdentity, ChannelDocument } from "@/types/RealtimeProtocol";
import { RealtimeManager } from "@/services/realtime/RealtimeManager";
import { RealtimePayload } from "@/services/realtime/types";
import { toCallerIdentity } from "@/helpers/callerHelpers";

function parseChannelDoc(
  channelId: string,
  raw: Record<string, unknown>,
): ChannelDocument {
  const activeCall = (raw.active_call as Record<string, unknown>) || {};
  return {
    id: (raw.id as string) || channelId,
    owner: (raw.owner as string) || (raw.owner_uid as string) || "",
    hosts: (raw.hosts as string[]) || [],
    is_ephemeral: Boolean(raw.is_ephemeral),
    expires_at: raw.expires_at as number | undefined,
    approved_senders:
      (raw.approved_senders as CallerIdentity[]) ||
      (activeCall.approved_senders as CallerIdentity[]) ||
      [],
    incoming_callers:
      (raw.incoming_callers as CallerIdentity[]) ||
      (activeCall.incoming_callers as CallerIdentity[]) ||
      [],
    updated_at:
      (raw.updated_at as number) ||
      (activeCall.updated_at as number) ||
      Date.now(),
    created_at: raw.created_at as number | undefined,
  };
}

export class ChannelService {
  /**
   * Subscribe to real-time updates for a specific channel document.
   */
  static subscribeToChannel(
    channelId: string,
    onUpdate: (channel: ChannelDocument | null) => void,
    onError?: (error: Error) => void,
  ): () => void {
    const normalizedId = channelId.trim().toLowerCase();
    const channelRef = doc(db, "channels", normalizedId);

    return onSnapshot(
      channelRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const docData = parseChannelDoc(
            normalizedId,
            snapshot.data() as Record<string, unknown>,
          );
          onUpdate(docData);
        } else {
          onUpdate(null);
        }
      },
      (error) => {
        console.warn(
          `ChannelService: Error subscribing to ${normalizedId} (falling back to WebSocket events):`,
          error,
        );
        onError?.(error);
      },
    );
  }

  /**
   * Query all channels owned or hosted by a given user UID.
   */
  static async getUserChannels(uid: string): Promise<ChannelDocument[]> {
    if (!uid) return [];
    try {
      const channelsCol = collection(db, "channels");
      const qHosts = query(channelsCol, where("hosts", "array-contains", uid));
      const snapHosts = await getDocs(qHosts);
      const results: ChannelDocument[] = [];
      const seenIds = new Set<string>();

      snapHosts.forEach((d) => {
        seenIds.add(d.id);
        results.push(
          parseChannelDoc(d.id, d.data() as Record<string, unknown>),
        );
      });

      const qOwner = query(channelsCol, where("owner", "==", uid));
      const snapOwner = await getDocs(qOwner);
      snapOwner.forEach((d) => {
        if (!seenIds.has(d.id)) {
          seenIds.add(d.id);
          results.push(
            parseChannelDoc(d.id, d.data() as Record<string, unknown>),
          );
        }
      });

      return results;
    } catch (err) {
      console.error(
        `ChannelService: Error querying user channels for ${uid}:`,
        err,
      );
      return [];
    }
  }

  /**
   * Subscribe to real-time updates for all channels hosted by a user UID.
   */
  static subscribeToUserChannels(
    uid: string,
    onUpdate: (channels: ChannelDocument[]) => void,
    onError?: (error: Error) => void,
  ): () => void {
    if (!uid) {
      onUpdate([]);
      return () => {};
    }

    const channelsCol = collection(db, "channels");
    const q = query(channelsCol, where("hosts", "array-contains", uid));

    return onSnapshot(
      q,
      (snapshot) => {
        const channels: ChannelDocument[] = [];
        snapshot.forEach((d) => {
          channels.push(
            parseChannelDoc(d.id, d.data() as Record<string, unknown>),
          );
        });
        onUpdate(channels);
      },
      (error) => {
        console.error(
          `ChannelService: Error subscribing to user channels for ${uid}:`,
          error,
        );
        onError?.(error);
      },
    );
  }

  /**
   * Ensure a permanent channel document exists for an authenticated user.
   */
  static async ensureUserChannel(
    uid: string,
    usernameOrEmail: string,
  ): Promise<ChannelDocument> {
    const normalizedId = usernameOrEmail.trim().toLowerCase();
    const channelRef = doc(db, "channels", normalizedId);
    const snap = await getDoc(channelRef);

    if (snap.exists()) {
      return snap.data() as ChannelDocument;
    }

    const newChannel: ChannelDocument = {
      id: normalizedId,
      owner: uid,
      hosts: [uid],
      is_ephemeral: false,
      approved_senders: [],
      incoming_callers: [],
      updated_at: Date.now(),
      created_at: Date.now(),
    };

    await setDoc(channelRef, newChannel, { merge: true });
    return newChannel;
  }

  /**
   * Auto-provision an ephemeral channel with a 1-hour TTL for a guest user.
   */
  static async ensureGuestChannel(
    guestNameOrUid: string,
    guestUid?: string,
  ): Promise<ChannelDocument> {
    const normalizedId = guestNameOrUid.trim().toLowerCase();
    const owner = guestUid || normalizedId;
    const channelRef = doc(db, "channels", normalizedId);

    try {
      const snap = await getDoc(channelRef);

      if (snap.exists()) {
        return snap.data() as ChannelDocument;
      }

      const newChannel: ChannelDocument = {
        id: normalizedId,
        owner: owner,
        hosts: [owner],
        is_ephemeral: true,
        expires_at: Math.floor(Date.now() / 1000) + 3600, // 1 hour TTL
        approved_senders: [],
        incoming_callers: [],
        updated_at: Date.now(),
        created_at: Date.now(),
      };

      await setDoc(channelRef, newChannel, { merge: true });
      return newChannel;
    } catch (err) {
      console.debug(
        `ChannelService.ensureGuestChannel: Direct Firestore write not permitted or unavailable for ${normalizedId}:`,
        err,
      );
      return {
        id: normalizedId,
        owner: owner,
        hosts: [owner],
        is_ephemeral: true,
        expires_at: Math.floor(Date.now() / 1000) + 3600,
        approved_senders: [],
        incoming_callers: [],
        updated_at: Date.now(),
        created_at: Date.now(),
      };
    }
  }

  /**
   * Send a knock to a channel.
   * Dual-publishes request_call over realtime transport and updates Firestore.
   */
  static async knockChannel(
    channelId: string,
    caller: CallerIdentity,
    messagePreview?: {
      title?: string;
      speech?: string;
      color?: string;
      emotion?: string;
    },
  ): Promise<void> {
    const normalizedId = channelId.trim().toLowerCase();
    const callerObj = toCallerIdentity(caller);

    // 1. Immediate real-time knock event via WebSocket / PubNub
    try {
      RealtimeManager.getInstance().subscribe([normalizedId]);
      await RealtimeManager.getInstance().publish(normalizedId, {
        type: "request_call",
        caller: callerObj,
        senderID: callerObj.name || callerObj.username || callerObj.uuid,
        senderSessionId: callerObj.session_id,
        speech: messagePreview?.speech || "Connecting to robot",
        emotion: messagePreview?.emotion || "Neutral",
        color: messagePreview?.color || "Light Blue",
        title: messagePreview?.title || "Knock",
      } as unknown as RealtimePayload);
    } catch (rtErr) {
      console.warn(
        `ChannelService: Realtime knock publish failed for ${normalizedId}:`,
        rtErr,
      );
    }

    // 2. Direct Firestore update
    const channelRef = doc(db, "channels", normalizedId);

    try {
      await updateDoc(channelRef, {
        incoming_callers: arrayUnion(callerObj),
        updated_at: Date.now(),
      });
      return;
    } catch (firestoreError: unknown) {
      const err = firestoreError as { code?: string; message?: string } | null;
      const isNotFound =
        err?.code === "not-found" ||
        String(err?.message || "")
          .toLowerCase()
          .includes("no document to update") ||
        String(err?.message || "")
          .toLowerCase()
          .includes("not found");

      if (isNotFound) {
        try {
          const snap = await getDoc(channelRef);
          if (!snap.exists()) {
            const newChannel: ChannelDocument = {
              id: normalizedId,
              owner: auth.currentUser?.uid || callerObj.uuid,
              hosts: [auth.currentUser?.uid || callerObj.uuid],
              is_ephemeral: !callerObj.email,
              approved_senders: [],
              incoming_callers: [callerObj],
              updated_at: Date.now(),
              created_at: Date.now(),
            };
            await setDoc(channelRef, newChannel, { merge: true });
            return;
          }
        } catch (createError) {
          console.debug(
            `ChannelService: Channel auto-provisioning skipped for ${normalizedId}:`,
            createError,
          );
        }
      }

      console.debug(
        `ChannelService: Direct Firestore knock not permitted or unavailable for ${normalizedId}:`,
        firestoreError,
      );
    }
  }

  /**
   * Approve a caller on a channel, moving them to approved_senders.
   * Dual-publishes accept_caller over realtime transport.
   */
  static async approveCaller(
    channelId: string,
    caller: CallerIdentity | string,
  ): Promise<void> {
    const normalizedId = channelId.trim().toLowerCase();
    const callerObj = toCallerIdentity(caller);
    const channelRef = doc(db, "channels", normalizedId);

    try {
      const snap = await getDoc(channelRef);

      if (!snap.exists()) {
        const newChannel: ChannelDocument = {
          id: normalizedId,
          owner: auth.currentUser?.uid || "anonymous",
          hosts: [auth.currentUser?.uid || "anonymous"],
          is_ephemeral: false,
          approved_senders: [callerObj],
          incoming_callers: [],
          updated_at: Date.now(),
          created_at: Date.now(),
        };
        await setDoc(channelRef, newChannel, { merge: true });
      } else {
        const data = snap.data() as Record<string, unknown>;
        const activeCall = (data.active_call as Record<string, unknown>) || {};
        const currentApproved: CallerIdentity[] =
          (data.approved_senders as CallerIdentity[]) ||
          (activeCall.approved_senders as CallerIdentity[]) ||
          [];
        const currentIncoming: CallerIdentity[] =
          (data.incoming_callers as CallerIdentity[]) ||
          (activeCall.incoming_callers as CallerIdentity[]) ||
          [];

        const matches = (c: CallerIdentity) => {
          if (!c) return false;
          const cUuid = (c.uuid || "").toLowerCase();
          const cUname = (c.username || "").toLowerCase();
          const cEmail = (c.email || "").toLowerCase();
          return (
            (cUuid && cUuid === callerObj.uuid.toLowerCase()) ||
            (callerObj.username &&
              cUname &&
              cUname === callerObj.username.toLowerCase()) ||
            (callerObj.email &&
              cEmail &&
              cEmail === callerObj.email.toLowerCase())
          );
        };

        const updatedApproved = [
          ...currentApproved.filter((c) => !matches(c)),
          callerObj,
        ];
        const updatedIncoming = currentIncoming.filter((c) => !matches(c));

        await updateDoc(channelRef, {
          approved_senders: updatedApproved,
          incoming_callers: updatedIncoming,
          updated_at: Date.now(),
        });
      }
    } catch (err) {
      console.debug(
        `ChannelService.approveCaller: Direct Firestore update not permitted or unavailable for ${normalizedId} (handled via realtime):`,
        err,
      );
    }

    // Dual-publish accept_caller over RealtimeManager to both room channel and caller channels
    const approveTargets = new Set<string>([normalizedId]);
    if (callerObj.uuid) approveTargets.add(callerObj.uuid.toLowerCase());
    if (callerObj.username)
      approveTargets.add(callerObj.username.toLowerCase());
    if (callerObj.name) approveTargets.add(callerObj.name.toLowerCase());
    if (callerObj.email) approveTargets.add(callerObj.email.toLowerCase());

    await Promise.all(
      Array.from(approveTargets).map((t) =>
        RealtimeManager.getInstance()
          .publish(t, {
            type: "accept_caller",
            caller: callerObj,
          } as unknown as RealtimePayload)
          .catch((rtErr) => {
            console.warn(
              `ChannelService.approveCaller: Realtime publish failed for ${t}:`,
              rtErr,
            );
          }),
      ),
    );
  }

  /**
   * Decline a caller from incoming_callers.
   * Dual-publishes deny_caller over realtime transport.
   */
  static async declineCaller(
    channelId: string,
    callerUuid: string,
  ): Promise<void> {
    const normalizedId = channelId.trim().toLowerCase();
    const idLower = callerUuid.trim().toLowerCase();
    const channelRef = doc(db, "channels", normalizedId);

    try {
      const snap = await getDoc(channelRef);
      if (snap.exists()) {
        const matchesId = (c: CallerIdentity) => {
          if (!c) return false;
          const cUuid = (c.uuid || "").toLowerCase();
          const cUname = (c.username || "").toLowerCase();
          const cEmail = (c.email || "").toLowerCase();
          const cName = (c.name || "").toLowerCase();
          return (
            cUuid === idLower ||
            cUname === idLower ||
            cEmail === idLower ||
            cName === idLower
          );
        };

        const data = snap.data() as Record<string, unknown>;
        const activeCall = (data.active_call as Record<string, unknown>) || {};
        const currentIncoming: CallerIdentity[] =
          (data.incoming_callers as CallerIdentity[]) ||
          (activeCall.incoming_callers as CallerIdentity[]) ||
          [];
        const updatedIncoming = currentIncoming.filter((c) => !matchesId(c));

        await updateDoc(channelRef, {
          incoming_callers: updatedIncoming,
          updated_at: Date.now(),
        });
      }
    } catch (err) {
      console.debug(
        `ChannelService.declineCaller: Direct Firestore update not permitted or unavailable for ${normalizedId} (handled via realtime):`,
        err,
      );
    }

    const declineTargets = new Set<string>([normalizedId, idLower]);
    await Promise.all(
      Array.from(declineTargets).map((t) =>
        RealtimeManager.getInstance()
          .publish(t, {
            type: "deny_caller",
            caller: { uuid: idLower },
          } as unknown as RealtimePayload)
          .catch((rtErr) => {
            console.warn(
              `ChannelService.declineCaller: Realtime publish failed for ${t}:`,
              rtErr,
            );
          }),
      ),
    );
  }

  /**
   * End an active call for an approved sender.
   * Dual-publishes end_call over realtime transport.
   */
  static async endCall(channelId: string, callerUuid: string): Promise<void> {
    const normalizedId = channelId.trim().toLowerCase();
    const idLower = callerUuid.trim().toLowerCase();
    const channelRef = doc(db, "channels", normalizedId);

    try {
      const snap = await getDoc(channelRef);
      if (snap.exists()) {
        const matchesId = (c: CallerIdentity) => {
          if (!c) return false;
          const cUuid = (c.uuid || "").toLowerCase();
          const cUname = (c.username || "").toLowerCase();
          const cEmail = (c.email || "").toLowerCase();
          const cName = (c.name || "").toLowerCase();
          return (
            cUuid === idLower ||
            cUname === idLower ||
            cEmail === idLower ||
            cName === idLower
          );
        };

        const data = snap.data() as Record<string, unknown>;
        const activeCall = (data.active_call as Record<string, unknown>) || {};
        const currentApproved: CallerIdentity[] =
          (data.approved_senders as CallerIdentity[]) ||
          (activeCall.approved_senders as CallerIdentity[]) ||
          [];
        const updatedApproved = currentApproved.filter((c) => !matchesId(c));

        await updateDoc(channelRef, {
          approved_senders: updatedApproved,
          updated_at: Date.now(),
        });
      }
    } catch (err) {
      console.debug(
        `ChannelService.endCall: Direct Firestore update not permitted or unavailable for ${normalizedId} (handled via realtime):`,
        err,
      );
    }

    try {
      await RealtimeManager.getInstance().publish(normalizedId, {
        type: "end_call",
        caller: { uuid: idLower },
      } as unknown as RealtimePayload);
    } catch (rtErr) {
      console.warn(
        `ChannelService.endCall: Realtime publish failed for ${normalizedId}:`,
        rtErr,
      );
    }
  }
}
